// The electrical schematic's LAYOUT (docs/claude/elecSchematic.md): a view of
// the model (elecSchematic.ts) placed on paper, in paper mm (top-left origin, Y
// down) at a FIXED print size — the object is as big as its content. Pure (no
// DOM): it gives every rectangle and polyline the sheet draws and picks.
//
// Vertically the grid is a stack of LINES of boxes, two per row (its slots, then
// its bars: the junction boxes, the Spa Manager and a box centred under the
// boxes it is wired to), with a CORRIDOR above each line where wires jog. A wire
// leaves its upper box's bottom and lands on its lower box's top, jogging once in
// the corridor at the top of its lower end's row, in a lane of its own.

import type {
  ElecBoxAngle,
  ElecCellId,
  ElecRowId,
  ElecView,
  SchematicLineStyle,
  Vec2,
} from '../shared/types';
import { segCross } from './lineHops';
import { unreachable } from '../shared/exhaustive';
import {
  compareElecBoxesAuto,
  compareElecNumbers,
  ELEC_CLIENT,
  ELEC_COLUMN_ROWS,
  ELEC_ROWS,
  ELEC_STENAL,
  type ElecBox,
  elecCellId,
  type ElecModel,
  elecNum,
  elecOutOfOrder,
  type ElecTr,
  type ElecWire,
  parseElecCell,
} from './elecSchematic';

// ---- Fixed print size (decision 14) -----------------------------------------------------

export const ELEC_MM_PER_PT = 25.4 / 72;
/** The text: 7 pt. */
export const ELEC_TEXT_PT = 7;
export const ELEC_TEXT_MM = ELEC_TEXT_PT * ELEC_MM_PER_PT;
/** A line of text, baseline to baseline. */
export const ELEC_LINE_MM = ELEC_TEXT_MM * 1.25;
/** A device box's least width (its text runs up it). */
export const ELEC_BOX_W = 6;
/** A vertical box's least height. */
export const ELEC_BOX_MIN_LEN = 14;
/** A horizontal box's least height and width. */
export const ELEC_BAR_H = 5;
export const ELEC_BAR_MIN_W = 16;
/** Text inset in a box. */
export const ELEC_BOX_PAD = 0.8;
/** Between two wires landing on one edge of a box: what it grows by per wire. */
export const ELEC_WIRE_PITCH = 3;
/** Between two jogs in a corridor. */
export const ELEC_LANE = 1.5;
/** Between two boxes in a cell, and a box and its cell's side. */
export const ELEC_BOX_GAP = 2;
export const ELEC_CELL_PAD = 3;
/** The row headers' width (their text runs up), the column headers' height. */
export const ELEC_ROW_HEAD_W = 8;
export const ELEC_COL_HEAD_H = 6;
export const ELEC_ROW_MIN_H = 12;
export const ELEC_COL_MIN_W = 20;
export const ELEC_PLANT_MIN_W = 30;
/** A plant room's name over its cell. */
export const ELEC_LABEL_H = ELEC_LINE_MM + 1;
/** The gap at the top of a row, and its foot. */
export const ELEC_ROW_TOP_GAP = 2;
export const ELEC_ROW_FOOT = 2;
/** The table's and the legend's gap from the grid, and their rows. */
export const ELEC_ASIDE_GAP = 6;
export const ELEC_TABLE_ROW_H = ELEC_LINE_MM + 1.2;
export const ELEC_SWATCH = 4;
/** An end dot's radius. */
export const ELEC_DOT_R = 0.5;
/** A bridge's radius: the hump a wire makes over one it crosses. */
export const ELEC_BRIDGE_R = 0.9;
/** A wire's number badge: its text this much of the schematic's. */
export const ELEC_BADGE_SCALE = 0.85;
/** Room kept round a badge: from another wire, from another badge or a box, and
 *  from its own run's ends. */
export const ELEC_BADGE_CLEAR = 0.3;
export const ELEC_BADGE_GAP = 0.2;
export const ELEC_BADGE_END = 0.6;
/** A badge's size across its wire. */
const BADGE_ACROSS = ELEC_TEXT_MM * ELEC_BADGE_SCALE + 0.4;
/** An off-sheet note: as big as a badge's text, this far out from the grid's
 *  side, its baseline this far above its wire; two on one side this far apart
 *  down the sheet, at least. */
const NOTE_OUT = 0.8, NOTE_UP = 0.5, NOTE_H = ELEC_TEXT_MM * ELEC_BADGE_SCALE, NOTE_STEP = NOTE_H + 0.3;

export interface Rect { x: number; y: number; w: number; h: number }

export interface ElecLayoutOptions {
  tr?: ElecTr;
  /** A text's width (mm) at ELEC_TEXT_PT; absent = an estimate. */
  textWidth?: (s: string) => number;
  /** Where a box this view leaves out is shown (a sheet's name or number), for
   *  an off-sheet end's "→ sheet 2, wire E14". */
  whereIs?: (boxKey: string) => string | undefined;
}

export interface ElecLayoutBox {
  key: string;
  box: ElecBox;
  rect: Rect;
  /** Its text runs up or down it (`angle` 90 or 270): it is taller than wide. */
  vertical: boolean;
  /** Its text's direction (degrees clockwise): its own, or turned by hand. */
  angle: ElecBoxAngle;
  /** Its line's own direction: up a device box (270), along a bar (0). */
  autoAngle: ElecBoxAngle;
  lines: string[];
  /** Its line in the grid (two per row: slots, bars). */
  line: number;
  /** Not where the rules put it — the model's `moved`; a client-row box's in
   *  this view: out of the view's own automatic order (`clientAuto`). What the
   *  card's Back to its own place reads. */
  moved: boolean;
}

export interface ElecLayoutWire {
  key: string;
  wire: ElecWire;
  points: Vec2[];
  /** Its own (`ElecWireEdit.color`), else its installer's (decision 17). */
  color: string;
  /** Line width (paper mm) and dash: its own, else the schematic's. */
  thickness: number;
  style: SchematicLineStyle;
  /** Where it hops over a wire it crosses (`seg`: the index of its run). */
  bridges: Array<{ seg: number; at: Vec2 }>;
  /** Its end dots: both ends, or the one in this view. */
  dots: Vec2[];
  /** The same, each with the box it lands on: what an end dragged away moves. */
  ends: Array<{ at: Vec2; box: string }>;
  /** Its number, on a white ground over one of its runs (up an upright run, along
   *  a level one) where it covers no other wire, badge or box. */
  badge?: { at: Vec2; text: string; rect: Rect; vertical: boolean };
  /** The horizontal it jogs along (to drag). */
  jog?: { y: number; x0: number; x1: number };
  /** Its other end is in another view: it stops at the grid's side (`at`), its
   *  note (`text`, in `rect`) outside the grid at its height. */
  offSheet?: { at: Vec2; side: 'left' | 'right'; text: string; other: string; rect: Rect };
}

/** A cell; a plant room's with its name (`label`) in `labelRect`, at the cell's
 *  top where no wire runs down through it — `labelGround` when there is no such
 *  place: drawn on a white ground over the wires. */
export interface ElecLayoutCell { id: ElecCellId; row: ElecRowId; rect: Rect; color?: string; label?: string; labelRect?: Rect; labelGround?: true }
export interface ElecLayoutBorder { row: ElecRowId; after: string; x: number; y0: number; y1: number }
export interface ElecLayoutRow { id: ElecRowId; rect: Rect; header: { rect: Rect; text: string } }
export interface ElecLayoutColumn { id: string; header: Rect; body: Rect; text: string; color: string }
export interface ElecLayoutTable { rect: Rect; rowH: number; colW: number[]; header: string[]; rows: Array<{ key: string; cells: string[] }> }
export interface ElecLayoutLegend { rect: Rect; rowH: number; entries: Array<{ color: string; text: string; kind: 'installer' | 'column' }> }

export interface ElecLayout {
  bounds: Rect;
  grid: Rect;
  rows: ElecLayoutRow[];
  columns: ElecLayoutColumn[];
  cells: ElecLayoutCell[];
  borders: ElecLayoutBorder[];
  boxes: ElecLayoutBox[];
  wires: ElecLayoutWire[];
  table: ElecLayoutTable | null;
  legend: ElecLayoutLegend | null;
  /** The client row's boxes this view shows, in ITS automatic order: under
   *  what each is wired to, left to right (two under one place, and those wired
   *  to nothing above, after, in the model's automatic order) — with a client
   *  box it draws in another cell where it would stand in the row. Views
   *  differ: the band stands from the grid's left in each, the columns where
   *  the view puts them. */
  clientAuto: string[];
}

// ---- Which boxes a view shows (decision 27) -------------------------------------------------

const isColumnRow = (row: ElecRowId): boolean => ELEC_COLUMN_ROWS.includes(row);

/** The view's columns, in the model's order. */
export function elecViewColumns(model: ElecModel, view: Pick<ElecView, 'columns'>): string[] {
  const want = view.columns ? new Set(view.columns) : null;
  return model.columns.filter((c) => !want || want.has(c.id)).map((c) => c.id);
}

/** The boxes a view shows: those in its columns; a band or client box wired to
 *  one of them; and one wired to no column at all, through the band and client
 *  boxes it is wired to (the Spa Manager's supply), or always when those reach
 *  no column either. */
export function elecViewBoxes(model: ElecModel, view: Pick<ElecView, 'columns'>): Set<string> {
  const cols = new Set(elecViewColumns(model, view));
  const colOf = (b: ElecBox): string | null => { const c = parseElecCell(b.cell); return isColumnRow(c.row) ? c.key : null; };
  const adj = new Map<string, string[]>();
  for (const w of model.wires) {
    (adj.get(w.from) ?? adj.set(w.from, []).get(w.from)!).push(w.to);
    (adj.get(w.to) ?? adj.set(w.to, []).get(w.to)!).push(w.from);
  }
  const out = new Set<string>();
  const direct = new Map<string, Set<string>>();
  for (const b of model.boxes) {
    const c = colOf(b);
    if (c != null) { if (cols.has(c)) out.add(b.key); continue; }
    const s = new Set<string>();
    for (const n of adj.get(b.key) ?? []) { const nb = model.boxByKey.get(n); const nc = nb && colOf(nb); if (nc != null) s.add(nc); }
    direct.set(b.key, s);
    if ([...s].some((x) => cols.has(x))) out.add(b.key);
  }
  // The free ones: band / client boxes wired to no column.
  const free = [...direct].filter(([, s]) => s.size === 0).map(([k]) => k);
  const done = new Set<string>();
  for (const start of free) {
    if (done.has(start)) continue;
    const comp: string[] = [], queue = [start];
    done.add(start);
    let touches = false, touchesHere = false;
    for (let i = 0; i < queue.length; i++) {
      const k = queue[i];
      comp.push(k);
      for (const n of adj.get(k) ?? []) {
        const s = direct.get(n);
        if (!s) continue;
        if (s.size) { touches = true; if (out.has(n)) touchesHere = true; continue; }
        if (!done.has(n)) { done.add(n); queue.push(n); }
      }
    }
    if (touchesHere || !touches) for (const k of comp) out.add(k);
  }
  return out;
}

// ---- The layout -------------------------------------------------------------------------

const ROW_LINES: Record<ElecRowId, number[]> = { outside: [0, 1], cabin: [2, 3], ceiling: [4, 5], technical: [6, 7], client: [8] };
const LINES = 9;
const rowOfLine = (l: number): ElecRowId => ELEC_ROWS[Math.min(4, Math.floor(l / 2))];

interface Piece { b: ElecBox; w: number; h: number; vertical: boolean; angle: ElecBoxAngle; autoAngle: ElecBoxAngle; line: number; x: number; y: number; top: Landing[]; bottom: Landing[] }
interface Landing { wire: string; other: string | null; side?: 'left' | 'right'; x: number }
interface Route {
  w: ElecWire; upper: string; lower: string | null; u: boolean;
  stubEdge?: 'top' | 'bottom'; side?: 'left' | 'right'; other?: string; note?: string;
  corridor: number; lane: number; P?: Vec2; Q?: Vec2;
}

/** A box centred under the boxes above it: its width, the middle of those boxes,
 *  how far left and right it may stand and still be under all of them, where it
 *  would stand alone (`at`) and where it stands (`x`). */
interface Under { p: Piece; w: number; mid: number; min: number; max: number; at: number; x: number }

/** The centred boxes of one line side by side, never overlapping: from the left,
 *  each as near its own place as the one before allows, pulling those before it
 *  left when it would leave its boxes above; one that still cannot stay under
 *  them is left out. Returns those placed, left to right (their `x` set). */
function packUnder(list: Under[]): Under[] {
  const order = [...list].sort((a, b) => a.mid - b.mid || a.at - b.at);
  const placed: Under[] = [];
  for (const k of order) {
    const prev = placed[placed.length - 1];
    let x = Math.max(k.at, prev ? prev.x + prev.w + ELEC_BOX_GAP : -Infinity);
    if (x > k.max + 1e-9) {
      x = k.max;
      const moved: number[] = [];
      let right = x, ok = true;
      for (let i = placed.length - 1; i >= 0; i--) {
        const q = placed[i], nx = Math.min(q.x, right - ELEC_BOX_GAP - q.w);
        if (nx < q.min - 1e-9) { ok = false; break; }
        if (nx === q.x) break;
        moved.push(q.x); q.x = nx; right = nx;
      }
      if (!ok) { moved.forEach((ox, j) => { placed[placed.length - 1 - j].x = ox; }); continue; }
    }
    k.x = x;
    placed.push(k);
  }
  return placed;
}

/** A view of the schematic laid out in paper mm at its fixed print size. It is
 *  laid out at the page's origin and moved where the view stands, so the layout
 *  of a view moved (`translateElecLayout`) IS the view laid out there, to the
 *  last bit: no choice in the passes turns on where the view stands (rounding
 *  once tipped a box past a wire, a badge onto another run). */
export function layoutElecSchematic(model: ElecModel, view: ElecView, opts: ElecLayoutOptions = {}): ElecLayout {
  const rel = (b: ElecView['table']): ElecView['table'] => b && { ...b, x: b.x - view.x, y: b.y - view.y };
  const at0: ElecView = { ...view, x: 0, y: 0, ...(view.table ? { table: rel(view.table) } : {}), ...(view.legend ? { legend: rel(view.legend) } : {}) };
  const L = layoutAtOrigin(model, at0, opts);
  return view.x || view.y ? translateElecLayout(L, view.x, view.y) : L;
}

function layoutAtOrigin(model: ElecModel, view: ElecView, opts: ElecLayoutOptions): ElecLayout {
  const tr: ElecTr = opts.tr ?? ((s) => s);
  const textW = (s: string): number => opts.textWidth?.(s) ?? s.length * ELEC_TEXT_MM * 0.5;
  const longest = (lines: string[]): number => lines.reduce((m, s) => Math.max(m, textW(s)), 0);
  const viewCols = elecViewColumns(model, view);
  const colIndex = new Map(model.columns.map((c, i) => [c.id, i]));
  const columns = model.columns.filter((c) => viewCols.includes(c.id));
  const inView = elecViewBoxes(model, view);
  const rowHidden = (r: ElecRowId): boolean => !!model.rows.find((x) => x.id === r)?.hidden;
  const lineVisible = (l: number): boolean => !rowHidden(rowOfLine(l));

  // ---- Wires in the view, and each box's line.
  const full = model.wires.filter((w) => inView.has(w.from) && inView.has(w.to));
  const stubs = model.wires.filter((w) => inView.has(w.from) !== inView.has(w.to));
  // The boxes in rows above each box is wired to: each once, however many lines.
  const higherNeighbours = new Map<string, string[]>();
  for (const w of full) {
    for (const [a, b] of [[w.from, w.to], [w.to, w.from]]) {
      const ba = model.boxByKey.get(a)!, bb = model.boxByKey.get(b)!;
      if (!isColumnRow(ba.row) || !isColumnRow(bb.row) || ELEC_ROWS.indexOf(bb.row) >= ELEC_ROWS.indexOf(ba.row)) continue;
      const list = higherNeighbours.get(a) ?? higherNeighbours.set(a, []).get(a)!;
      if (!list.includes(b)) list.push(b);
    }
  }
  const spanning = (b: ElecBox): boolean => isColumnRow(b.row) && (higherNeighbours.get(b.key)?.length ?? 0) >= 2;
  const isBar = (b: ElecBox): boolean => b.row !== 'client' && (!!b.junction || spanning(b));
  const lineOf = (b: ElecBox): number => ROW_LINES[b.row][isBar(b) && b.row !== 'client' ? 1 : 0];

  const pieces = new Map<string, Piece>();
  for (const b of model.boxes) {
    if (!inView.has(b.key)) continue;
    const autoAngle: ElecBoxAngle = !isBar(b) && b.row !== 'client' ? 270 : 0;
    const angle = b.rotation ?? autoAngle;
    pieces.set(b.key, { b, w: 0, h: 0, vertical: angle === 90 || angle === 270, angle, autoAngle, line: lineOf(b), x: 0, y: 0, top: [], bottom: [] });
  }
  const routes: Route[] = [];
  for (const w of full) {
    const a = pieces.get(w.from)!, c = pieces.get(w.to)!;
    const u = a.line === c.line;
    const [up, lo] = a.line <= c.line ? [a, c] : [c, a];
    routes.push({ w, upper: up.b.key, lower: lo.b.key, u, corridor: -1, lane: -1 });
    up.bottom.push({ wire: w.key, other: lo.b.key, x: 0 });
    (u ? lo.bottom : lo.top).push({ wire: w.key, other: up.b.key, x: 0 });
  }
  for (const w of stubs) {
    const here = pieces.get(w.from) ?? pieces.get(w.to)!;
    const otherKey = here.b.key === w.from ? w.to : w.from;
    const ob = model.boxByKey.get(otherKey)!;
    const oc = parseElecCell(ob.cell);
    const first = Math.min(...viewCols.map((c) => colIndex.get(c) ?? 0));
    const side: 'left' | 'right' = isColumnRow(oc.row) && (colIndex.get(oc.key) ?? 0) < first ? 'left' : 'right';
    const edge: 'top' | 'bottom' = lineOf(ob) < here.line ? 'top' : 'bottom';
    const where = opts.whereIs?.(otherKey);
    const note = where ? tr('→ sheet {sheet}, wire {n}').replace('{sheet}', where).replace('{n}', w.number) : tr('→ wire {n}').replace('{n}', w.number);
    routes.push({ w, upper: here.b.key, lower: null, u: false, stubEdge: edge, side, other: otherKey, note, corridor: -1, lane: -1 });
    here[edge].push({ wire: w.key, other: null, side, x: 0 });
  }

  // ---- Sizes (decision 19): a least size, a little more per wire on an edge.
  for (const p of pieces.values()) {
    const lines = p.b.lines, tw = longest(lines);
    const across = lines.length * ELEC_LINE_MM + 2 * ELEC_BOX_PAD;
    const perWire = Math.max(p.top.length, p.bottom.length) * ELEC_WIRE_PITCH;
    if (p.vertical) { p.w = Math.max(ELEC_BOX_W, across, perWire); p.h = Math.max(ELEC_BOX_MIN_LEN, tw + 2 * ELEC_BOX_PAD); }
    else { p.h = Math.max(ELEC_BAR_H, across); p.w = Math.max(ELEC_BAR_MIN_W, tw + 2 * ELEC_BOX_PAD, perWire); }
  }
  const baseW = new Map([...pieces].map(([k, p]) => [k, p.w]));
  const lineH: number[] = Array(LINES).fill(0);
  for (const p of pieces.values()) lineH[p.line] = Math.max(lineH[p.line], p.h);
  for (const p of pieces.values()) if (p.vertical) p.h = lineH[p.line];

  // ---- Across: the columns' rows, iterated until the columns are wide enough.
  // The notes of the wires off the sheet to the left stand before the row
  // headers, outside the grid: the grid stands right of the view's corner by
  // the widest (decision 27).
  const noteW = (s: string): number => textW(s) * ELEC_BADGE_SCALE;
  const leftNotes = routes.filter((r) => r.lower == null && r.side === 'left').map((r) => noteW(r.note!));
  const gridX = view.x + (leftNotes.length ? Math.max(...leftNotes) + NOTE_OUT : 0);
  const gx0 = gridX + ELEC_ROW_HEAD_W;
  const cellBoxes = (cell: ElecCellId): Piece[] => model.boxes.filter((b) => b.cell === cell && pieces.has(b.key)).map((b) => pieces.get(b.key)!);
  const width = new Map(columns.map((c) => [c.id, Math.max(ELEC_COL_MIN_W, textW(c.name) + 2 * ELEC_CELL_PAD)]));
  const colX = new Map<string, number>();
  const passing = (row: ElecRowId): Array<[number, number]> => {
    // Boxes above this row with a wire running down past it.
    const last = ROW_LINES[row][ROW_LINES[row].length - 1], first = ROW_LINES[row][0];
    const out: Array<[number, number]> = [];
    for (const r of routes) {
      if (!r.lower) continue;
      const up = pieces.get(r.upper)!, lo = pieces.get(r.lower)!;
      if (up.line < first && lo.line > last) out.push([up.x, up.x + up.w]);
    }
    return out;
  };
  // Where those wires can leave their boxes: between the first and the last of
  // each one's bottom landings (its middle, for a box with one wire).
  const passingRuns = (row: ElecRowId): Array<[number, number, Piece]> => {
    const last = ROW_LINES[row][ROW_LINES[row].length - 1], first = ROW_LINES[row][0];
    const out: Array<[number, number, Piece]> = [];
    for (const r of routes) {
      if (!r.lower) continue;
      const up = pieces.get(r.upper)!, lo = pieces.get(r.lower)!, k = up.bottom.length;
      if (up.line < first && lo.line > last) out.push([up.x + (up.w * 0.5) / k, up.x + (up.w * (k - 0.5)) / k, up]);
    }
    return out;
  };
  const placeAvoiding = (w: number, start: number, blocked: Array<[number, number]>): number => {
    let x = start;
    for (let moved = true, n = 0; moved && n < 1000; n++) {
      moved = false;
      for (const [a, b] of blocked) if (x < b + ELEC_BOX_GAP && x + w > a - ELEC_BOX_GAP) { x = b + ELEC_BOX_GAP; moved = true; }
    }
    return x;
  };
  // A box centred under the boxes above it (decision 20) that would stand across
  // a wire running down past it, or could not stay under them inside the grid,
  // stands aside instead, like any bar. So, for good, does one whose span (where
  // it stands or wants to) still widens after a few passes while the columns
  // grow: the boxes it pushes aside spread the boxes above it, it widens again,
  // and the passes would never settle.
  const uncentred = new Set<string>(), widening = new Set<string>();
  let noCentring = false;
  const centred = (b: ElecBox): boolean => !noCentring && spanning(b) && !uncentred.has(b.key) && !widening.has(b.key);
  // Where each centred box wants to stand (from the pass before): a box right of
  // its middle with a wire running down past it steps aside, so it can.
  let wanted: Array<{ key: string; row: ElecRowId; a: number; b: number; mid: number }> = [], wantedKey = '';
  const passesRow = (p: Piece, row: ElecRowId): boolean => {
    const first = ROW_LINES[row][0], last = ROW_LINES[row][ROW_LINES[row].length - 1];
    return p.line < first && routes.some((r) => r.upper === p.b.key && r.lower != null && pieces.get(r.lower)!.line > last);
  };
  const aside = (p: Piece, cursor: number): Array<[number, number]> => wanted
    .filter((s) => cursor >= s.mid && !higherNeighbours.get(s.key)?.includes(p.b.key) && passesRow(p, s.row))
    .map((s) => [s.a, s.b]);
  // A centred box stands where its boxes above are: one under a box of a column
  // right of its own does not widen its own column, which would move that box,
  // and it with it.
  const columnOf = (k: string): number => colIndex.get(parseElecCell(model.boxByKey.get(k)!.cell).key) ?? -1;
  const centredRightOf = (p: Piece, col: string): boolean => centred(p.b) && higherNeighbours.get(p.b.key)!.some((k) => columnOf(k) > (colIndex.get(col) ?? -1));
  // On each such box the passes start again from the columns' own widths.
  const width0 = new Map(width);
  const widest = new Map<string, number>();
  let since = 0, grewLast = false;
  const restart = (): void => {
    for (const [k, v] of width0) width.set(k, v);
    wanted = []; wantedKey = ''; widest.clear(); since = -1; grewLast = false;
  };
  const needOf = (c: { id: string }): number => {
    let need = 0;
    for (const row of ELEC_COLUMN_ROWS) for (const p of cellBoxes(elecCellId(row, c.id))) {
      if (centredRightOf(p, c.id)) continue;
      need = Math.max(need, p.x + p.w + ELEC_CELL_PAD - colX.get(c.id)!);
    }
    return need;
  };
  // Once settled, a column wider than its boxes need (grown in an early pass,
  // before the columns had moved apart) gives the room back, and the passes
  // settle again from there; the narrowest of these goes on.
  type Settled = { total: number; width: Map<string, number>; colX: Map<string, number>; at: Map<string, [number, number]>; uncentred: Set<string>; widening: Set<string>; noCentring: boolean };
  let best = null as Settled | null, rounds = 0;
  /** Keeps the layout just settled if it is the narrowest; true to settle again. */
  const settled = (): boolean => {
    const total = columns.reduce((t, c) => t + width.get(c.id)!, 0);
    if (!best || total < best.total - 1e-6) {
      best = { total, width: new Map(width), colX: new Map(colX), at: new Map([...pieces].map(([k, p]) => [k, [p.x, p.w]])), uncentred: new Set(uncentred), widening: new Set(widening), noCentring };
    }
    const tight = new Map(columns.map((c) => [c.id, Math.max(width0.get(c.id)!, needOf(c))]));
    if (++rounds >= 3 || columns.every((c) => tight.get(c.id)! > width.get(c.id)! - 1e-6)) return false;
    for (const [k, v] of tight) width.set(k, v);
    since = -1; grewLast = false;
    return true;
  };
  for (let iter = 0; iter < 80; iter++, since++) {
    // The last resort, while the columns still grow: without centring, they settle.
    if ((since === 16 || iter === 60) && !noCentring) { noCentring = true; restart(); }
    let x = gx0;
    for (const c of columns) { colX.set(c.id, x); x += width.get(c.id)!; }
    uncentred.clear(); // decided afresh on each pass, the last one on where the boxes end up
    for (const p of pieces.values()) if (spanning(p.b)) p.w = baseW.get(p.b.key)!;
    const want: typeof wanted = [];
    for (const row of ELEC_COLUMN_ROWS) {
      if (rowHidden(row)) continue;
      const pass = passing(row), runs = passingRuns(row);
      const barsBlocked: Array<[number, number]> = [];
      // Decision 20: a box wired to boxes above stands centred under them.
      const under: Under[] = [];
      for (const c of columns) {
        for (const p of cellBoxes(elecCellId(row, c.id))) {
          if (p.line !== ROW_LINES[row][1] || !centred(p.b)) continue;
          const xs = higherNeighbours.get(p.b.key)!.map((k) => pieces.get(k)).filter((q): q is Piece => !!q).map((q) => q.x + q.w / 2);
          const lo = Math.min(...xs), hi = Math.max(...xs), mid = (lo + hi) / 2;
          // Centred as far as the grid's side allows, still under every box above.
          const w = Math.max(baseW.get(p.b.key)!, hi - lo + 2 * ELEC_WIRE_PITCH);
          const min = Math.max(hi + ELEC_WIRE_PITCH - w, gx0 + ELEC_CELL_PAD), max = lo - ELEC_WIRE_PITCH;
          if (min > max + 1e-9) { uncentred.add(p.b.key); continue; }
          const at = Math.max(mid - w / 2, min);
          under.push({ p, w, mid, min, max, at, x: at });
        }
      }
      // Side by side where their spans meet; one standing across a wire running
      // down past it stands aside, and the others close up.
      const wants = new Map<string, Under>();
      for (let pool = under; ;) {
        const placed = packUnder(pool);
        for (const k of pool) if (!placed.includes(k)) { uncentred.add(k.p.b.key); }
        for (const k of placed) wants.set(k.p.b.key, { ...k });
        const crossed = (k: Under): Piece[] => runs.filter(([a, b]) => k.x < b + ELEC_WIRE_PITCH / 2 && k.x + k.w > a - ELEC_WIRE_PITCH / 2).map((r) => r[2]);
        const across = placed.filter((k) => crossed(k).length);
        // Only a box right of its middle steps aside (and none of its own boxes
        // above): one across a wire of any other box cannot stand centred however
        // the others step, so they do not step for it.
        const stays = (k: Under, q: Piece): boolean => q.x < k.mid || !!higherNeighbours.get(k.p.b.key)?.includes(q.b.key);
        for (const k of across) { uncentred.add(k.p.b.key); if (crossed(k).some((q) => stays(k, q))) wants.delete(k.p.b.key); }
        if (!across.length) {
          for (const k of placed) { k.p.w = k.w; k.p.x = k.x; barsBlocked.push([k.x, k.x + k.w]); }
          break;
        }
        pool = placed.filter((k) => !across.includes(k));
      }
      for (const [key, k] of wants) want.push({ key, row, a: k.x, b: k.x + k.w, mid: k.mid });
      const allBars: Array<[number, number]> = [...barsBlocked];
      for (const c of columns) {
        let cursor = colX.get(c.id)! + ELEC_CELL_PAD;
        for (const p of cellBoxes(elecCellId(row, c.id))) {
          if (p.line !== ROW_LINES[row][1] || centred(p.b)) continue;
          p.x = placeAvoiding(p.w, cursor, [...barsBlocked, ...pass, ...aside(p, cursor)]);
          cursor = p.x + p.w + ELEC_BOX_GAP;
          allBars.push([p.x, p.x + p.w]);
        }
      }
      for (const c of columns) {
        let cursor = colX.get(c.id)! + ELEC_CELL_PAD;
        for (const p of cellBoxes(elecCellId(row, c.id))) {
          if (p.line !== ROW_LINES[row][0]) continue;
          p.x = placeAvoiding(p.w, cursor, [...allBars, ...pass, ...aside(p, cursor)]);
          cursor = p.x + p.w + ELEC_BOX_GAP;
        }
      }
    }
    let grew = false;
    for (const c of columns) {
      const need = needOf(c);
      if (need > width.get(c.id)! + 1e-6) { width.set(c.id, need); grew = true; }
    }
    // Wider than it has been, on any pass before (two may take turns), while
    // the columns grow — on this pass or the one before: the box's span and the
    // columns may grow by turns, the boxes avoiding where it wanted to stand
    // widening a column one pass, its boxes above spreading the next.
    const more = (grew || grewLast) && since >= 3 ? want.filter((s) => s.b - s.a > (widest.get(s.key) ?? Infinity) + 1e-6) : [];
    for (const s of want) widest.set(s.key, Math.max(s.b - s.a, widest.get(s.key) ?? 0));
    if (more.length) { for (const s of more) widening.add(s.key); restart(); continue; }
    const key = want.map((s) => `${s.key}:${s.a.toFixed(3)}:${s.b.toFixed(3)}`).join('|');
    // Settled: no column moved, and the centred boxes want to stand where they
    // did — or, as their wanted spans can take turns, 7 passes on.
    if (!grew && (key === wantedKey || since >= 7)) { if (settled()) continue; break; }
    wanted = want; wantedKey = key; grewLast = grew;
  }
  if (best) {
    for (const [k, v] of best.width) width.set(k, v);
    for (const [k, v] of best.colX) colX.set(k, v);
    for (const [k, [x, w]] of best.at) { const p = pieces.get(k)!; p.x = x; p.w = w; }
    uncentred.clear(); best.uncentred.forEach((k) => uncentred.add(k));
    widening.clear(); best.widening.forEach((k) => widening.add(k));
    noCentring = best.noCentring;
  }
  const colsEnd = columns.reduce((x, c) => x + width.get(c.id)!, gx0);

  // ---- The Technical rooms band (decision 21): plant rooms side by side.
  const plantX = new Map<string, number>(), plantW = new Map<string, number>();
  {
    let x = gx0;
    const pass = passing('technical');
    for (const pr of model.plantRooms) {
      plantX.set(pr.id, x);
      const list = cellBoxes(elecCellId('technical', pr.id));
      let cursor = x + ELEC_CELL_PAD;
      const bars: Array<[number, number]> = [];
      for (const p of list) if (p.line === 7) { p.x = placeAvoiding(p.w, cursor, pass); cursor = p.x + p.w + ELEC_BOX_GAP; bars.push([p.x, p.x + p.w]); }
      cursor = x + ELEC_CELL_PAD;
      for (const p of list) if (p.line === 6) { p.x = placeAvoiding(p.w, cursor, [...bars, ...pass]); cursor = p.x + p.w + ELEC_BOX_GAP; }
      let need = Math.max(ELEC_PLANT_MIN_W, textW(pr.name) + 2 * ELEC_CELL_PAD, pr.width ?? 0);
      for (const p of list) need = Math.max(need, p.x + p.w + ELEC_CELL_PAD - x);
      plantW.set(pr.id, need);
      x += need;
    }
  }
  // A hidden band (empty) takes no room.
  const bandEnd = rowHidden('technical') ? gx0 : model.plantRooms.reduce((x, p) => x + plantW.get(p.id)!, gx0);

  // ---- Client provisions: under what each is wired to, as far as they can —
  // this view's own automatic order (`clientAuto`) — or in the row's hand order
  // once one is written, a box out of the view's own order `moved` in it.
  const centreOf = (k: string): number => { const p = pieces.get(k)!; return p.x + p.w / 2; };
  let clientAuto: string[] = [];
  const clientMoved = new Set<string>();
  {
    const list = cellBoxes('client');
    const target = (p: Piece): number => {
      const xs = [...p.top, ...p.bottom].filter((l) => l.other && pieces.get(l.other)!.line < 8).map((l) => centreOf(l.other!));
      return xs.length ? Math.min(...xs) : Infinity;
    };
    // A client box this view draws in another cell (dragged there) ranks where
    // it would stand in the row, so Back to its own place puts it there.
    const away = model.boxes.filter((b) => b.autoCell === 'client' && b.cell !== 'client' && pieces.has(b.key)).map((b) => pieces.get(b.key)!);
    const t = new Map([...list, ...away].map((p) => [p, target(p)]));
    // Two under one place: in the automatic order (never the hand order, which
    // would make the view's own order follow it).
    const auto = [...list, ...away].sort((a, b) => (t.get(a) === t.get(b) ? compareElecBoxesAuto(a.b, b.b) : t.get(a)! < t.get(b)! ? -1 : 1));
    const order = list.some((p) => p.b.order != null) ? list : auto.filter((p) => p.b.cell === 'client');
    let cursor = gx0 + ELEC_CELL_PAD;
    for (const p of order) {
      const at = t.get(p)!;
      p.x = Math.max(cursor, Number.isFinite(at) ? at - p.w / 2 : cursor);
      cursor = p.x + p.w + ELEC_BOX_GAP;
    }
    clientAuto = auto.map((p) => p.b.key);
    const rank = new Map(auto.map((p, i) => [p, i]));
    for (const p of order) if (p.b.cell !== p.b.autoCell) clientMoved.add(p.b.key);
    const home = order.filter((p) => p.b.cell === p.b.autoCell);
    for (const p of elecOutOfOrder(home, (q) => rank.get(q)!, (q) => !!q.b.dragged)) clientMoved.add(p.b.key);
  }
  let gridRight = Math.max(colsEnd, bandEnd, gx0 + ELEC_COL_MIN_W);
  for (const p of pieces.values()) gridRight = Math.max(gridRight, p.x + p.w + ELEC_CELL_PAD);
  if (columns.length) { const last = columns[columns.length - 1]; width.set(last.id, width.get(last.id)! + gridRight - colsEnd); }
  if (model.plantRooms.length) { const last = model.plantRooms[model.plantRooms.length - 1]; plantW.set(last.id, plantW.get(last.id)! + gridRight - bandEnd); }

  // ---- Landings (decision 19): spread evenly along the edge, in the order of
  // their other ends, so they cross as little as they can.
  const sideX = (l: Landing): number => (l.other ? centreOf(l.other) : l.side === 'left' ? -1e9 : 1e9);
  // A wire from a box of the same row jogs just above the edge, across the
  // landings between: it takes the end of the edge on its own side.
  const landKey = (p: Piece, l: Landing, edge: 'top' | 'bottom'): number => {
    const x = sideX(l), o = l.other ? pieces.get(l.other)! : null;
    if (edge !== 'top' || !o || o.line >= p.line || rowOfLine(o.line) !== rowOfLine(p.line)) return x;
    return x < p.x + p.w / 2 ? x - 1e6 : x + 1e6;
  };
  const spread = (p: Piece, list: Landing[], edge: 'top' | 'bottom'): void => {
    list.sort((a, b) => landKey(p, a, edge) - landKey(p, b, edge) || a.wire.localeCompare(b.wire));
    list.forEach((l, i) => { l.x = p.x + (p.w * (i + 0.5)) / list.length; });
  };
  for (const p of pieces.values()) spread(p, p.bottom, 'bottom');
  for (const p of pieces.values()) {
    spread(p, p.top, 'top');
    if (!centred(p.b)) continue;
    // A centred box takes each wire from above straight down, so far apart that
    // a number badge on one covers no other.
    const xs = p.top.map((l) => { const o = l.other && pieces.get(l.other)!.bottom.find((m) => m.wire === l.wire); return o ? Math.max(p.x + 0.3, Math.min(p.x + p.w - 0.3, o.x)) : l.x; });
    const sorted = [...xs].sort((a, b) => a - b);
    if (sorted.every((x, i) => !i || x - sorted[i - 1] > BADGE_ACROSS / 2 + ELEC_BADGE_CLEAR)) p.top.forEach((l, i) => { l.x = xs[i]; });
  }
  const landing = (key: string, wire: string, edge: 'top' | 'bottom'): number => pieces.get(key)![edge].find((l) => l.wire === wire)!.x;

  // ---- Corridors and lanes.
  const nextVisible = (l: number): number => { for (let k = l + 1; k < LINES; k++) if (lineVisible(k)) return k; return LINES; };
  const rowTopLine = (l: number): number => ROW_LINES[rowOfLine(l)][0];
  interface Item { r: Route; tops: number[]; bottoms: number[]; lo: number; hi: number }
  const corridors: Item[][] = Array.from({ length: LINES + 1 }, () => []);
  // A wire off the sheet to the left runs on across the row headers to its note,
  // so the two read as one.
  const edgeX = (side: 'left' | 'right'): number => (side === 'left' ? gridX : gridRight);
  for (const r of routes) {
    if (r.lower == null) {
      const p = pieces.get(r.upper)!;
      const x = landing(r.upper, r.w.key, r.stubEdge!);
      r.corridor = r.stubEdge === 'bottom' ? nextVisible(p.line) : rowTopLine(p.line);
      const ex2 = edgeX(r.side!);
      corridors[r.corridor].push({ r, tops: r.stubEdge === 'bottom' ? [x] : [], bottoms: r.stubEdge === 'top' ? [x] : [], lo: Math.min(x, ex2), hi: Math.max(x, ex2) });
      continue;
    }
    const up = pieces.get(r.upper)!, lo = pieces.get(r.lower)!;
    const xa = landing(r.upper, r.w.key, 'bottom'), xb = landing(r.lower, r.w.key, r.u ? 'bottom' : 'top');
    // A jog dragged by hand holds only between two lines: with both ends on one,
    // the wire turns under them in its lane like any other.
    if (r.w.straight || (!r.u && (r.w.jog != null || Math.abs(xa - xb) < 1e-6))) continue;
    r.corridor = r.u ? nextVisible(up.line) : rowOfLine(up.line) === rowOfLine(lo.line) ? lo.line : rowTopLine(lo.line);
    corridors[r.corridor].push({ r, tops: r.u ? [xa, xb] : [xa], bottoms: r.u ? [] : [xb], lo: Math.min(xa, xb), hi: Math.max(xa, xb) });
  }
  const lanesIn: number[] = corridors.map((items) => assignLanes(items));
  // Each lane's depth in its corridor: a lane below the one above, and a note's
  // height below any above it with an off-sheet note on the same side, so the
  // notes outside the grid stand clear of one another.
  const laneAt: number[][] = corridors.map((items, c) => {
    const sides = Array.from({ length: lanesIn[c] }, () => new Set<string>()), at: number[] = [];
    for (const it of items) if (it.r.lower == null) sides[it.r.lane].add(it.r.side!);
    for (let k = 0; k < lanesIn[c]; k++) {
      let y = (k ? at[k - 1] : 0) + ELEC_LANE;
      for (let j = 0; j < k; j++) if ([...sides[k]].some((s) => sides[j].has(s))) y = Math.max(y, at[j] + NOTE_STEP);
      at.push(y);
    }
    return at;
  });

  // ---- Down: rows, corridors, lines. A corridor's pads — above its lanes, below
  // them — open only where a wire's number badge finds no room (decision 17).
  const padTop: number[] = Array(LINES + 1).fill(0), padBottom: number[] = Array(LINES + 1).fill(0);
  const corrTop: number[] = Array(LINES + 1).fill(0), lineTop: number[] = Array(LINES).fill(0);
  const corrH = (c: number, base: number): number => padTop[c] + (lanesIn[c] ? laneAt[c][lanesIn[c] - 1] + ELEC_LANE : base) + padBottom[c];
  const rowRect = new Map<ElecRowId, Rect>();
  const gy0 = view.y + ELEC_COL_HEAD_H;
  const visibleRows = ELEC_ROWS.filter((r) => !rowHidden(r));
  const showNumbers = view.showNumbers !== false;
  const laneY = (r: Route): number => corrTop[r.corridor] + padTop[r.corridor] + laneAt[r.corridor][r.lane];
  // Where each wire's pad is: the corridor its run into a box's top comes down
  // through, or the one its runs out of box bottoms go down into.
  const padOf = new Map<string, { c: number; top: boolean }>();
  for (const r of routes) {
    const up = pieces.get(r.upper)!;
    if (r.lower == null) padOf.set(r.w.key, r.stubEdge === 'bottom' ? { c: nextVisible(up.line), top: true } : { c: up.line, top: false });
    else padOf.set(r.w.key, r.u ? { c: nextVisible(up.line), top: true } : { c: pieces.get(r.lower)!.line, top: false });
  }
  let gridBottom = gy0;
  const down = (): ElecLayoutWire[] => {
    let y = gy0;
    visibleRows.forEach((row, ri) => {
      const top = y;
      if (row === 'technical') y += ELEC_LABEL_H;
      ROW_LINES[row].forEach((l, i) => {
        corrTop[l] = y; y += corrH(l, i === 0 ? ELEC_ROW_TOP_GAP : lineH[l] ? ELEC_LANE : 0);
        lineTop[l] = y; y += lineH[l];
      });
      if (ri === visibleRows.length - 1) { corrTop[LINES] = y; y += corrH(LINES, 0); }
      y += ELEC_ROW_FOOT;
      const name = model.rows.find((r) => r.id === row)!.name;
      const h = Math.max(y - top, ELEC_ROW_MIN_H, textW(name) + 2 * ELEC_BOX_PAD, model.rowHeights[row] ?? 0);
      rowRect.set(row, { x: gridX, y: top, w: 0, h });
      y = top + h;
    });
    gridBottom = y;
    for (const p of pieces.values()) p.y = lineTop[p.line];

    // ---- The wires' polylines.
    const out: ElecLayoutWire[] = [];
    for (const r of routes) {
      const color = r.w.look.color;
      const look = { thickness: r.w.look.thickness, style: r.w.look.style, bridges: [] };
      const up = pieces.get(r.upper)!;
      if (r.lower == null) {
        const x = landing(r.upper, r.w.key, r.stubEdge!);
        const E = { x, y: r.stubEdge === 'bottom' ? up.y + up.h : up.y };
        const yj = laneY(r), ex = edgeX(r.side!), tw = noteW(r.note!);
        const rect = { x: r.side === 'left' ? gridX - NOTE_OUT - tw : ex + NOTE_OUT, y: yj - NOTE_UP - NOTE_H, w: tw, h: NOTE_H };
        out.push({ key: r.w.key, wire: r.w, points: [E, { x, y: yj }, { x: ex, y: yj }], color, ...look, dots: [E], ends: [{ at: E, box: r.upper }], jog: { y: yj, x0: Math.min(x, ex), x1: Math.max(x, ex) }, offSheet: { at: { x: ex, y: yj }, side: r.side!, text: r.note!, other: r.other!, rect } });
        continue;
      }
      const lo = pieces.get(r.lower)!;
      const P = { x: landing(r.upper, r.w.key, 'bottom'), y: up.y + up.h };
      const Q = { x: landing(r.lower, r.w.key, r.u ? 'bottom' : 'top'), y: r.u ? lo.y + lo.h : lo.y };
      let points: Vec2[], jog: ElecLayoutWire['jog'];
      if (r.w.straight || (!r.u && r.w.jog == null && Math.abs(P.x - Q.x) < 1e-6)) points = [P, Q];
      else {
        const yj = r.w.jog != null && !r.u ? P.y + r.w.jog * (Q.y - P.y) : laneY(r);
        points = [P, { x: P.x, y: yj }, { x: Q.x, y: yj }, Q];
        jog = { y: yj, x0: Math.min(P.x, Q.x), x1: Math.max(P.x, Q.x) };
      }
      out.push({ key: r.w.key, wire: r.w, points, color, ...look, dots: [P, Q], ends: [{ at: P, box: r.upper }, { at: Q, box: r.lower }], jog });
    }
    placeBridges(out);
    return out;
  };
  let wiresOut = down();
  // A row's cells side by side, each border moved (`dx`) as edited: where each
  // cell is across, and the borders drawn. The cells and the plant rooms' names
  // (drawn from their cells) both read it.
  const cellSpans = (row: ElecRowId, list: Array<{ key: string; x: number; w: number }>): { spans: Array<{ x: number; w: number }>; lines: Array<{ after: string; x: number }> } => {
    const spans = list.map((c) => ({ x: c.x, w: c.w })), lines: Array<{ after: string; x: number }> = [];
    for (let i = 0; i < spans.length - 1; i++) {
      const e = model.borders.find((b) => b.row === row && b.after === list[i].key);
      const a = spans[i], b = spans[i + 1];
      const x = Math.max(a.x + 2, Math.min(b.x + b.w - 2, a.x + a.w + (e?.dx ?? 0)));
      a.w = x - a.x; b.w += b.x - x; b.x = x;
      if (!e?.removed) lines.push({ after: list[i].key, x });
    }
    return { spans, lines };
  };
  const plantCells = (): Array<{ key: string; x: number; w: number }> => model.plantRooms.map((p) => ({ key: p.id, x: plantX.get(p.id)!, w: plantW.get(p.id)! }));
  // The plant rooms' names, each at its cell's top: from its left, past any wire
  // coming down through the band they stand in, to the first place clear of
  // them all; at the left on a white ground when the cell has none. Where the
  // wires run across never changes as the grid is laid out down again.
  const plantLabels = (): Array<{ rect: Rect; ground: boolean }> => {
    const r = rowRect.get('technical');
    if (!r) return [];
    const { spans } = cellSpans('technical', plantCells());
    const y0 = r.y + ELEC_BOX_PAD, y1 = y0 + ELEC_TEXT_MM;
    const runs: Array<{ x: number; half: number }> = [];
    for (const w of wiresOut) for (let i = 0; i + 1 < w.points.length; i++) {
      const a = w.points[i], b = w.points[i + 1];
      if (Math.abs(a.x - b.x) < 1e-9 && Math.min(a.y, b.y) < y1 && Math.max(a.y, b.y) > y0) runs.push({ x: a.x, half: w.thickness / 2 + ELEC_BADGE_CLEAR });
    }
    return model.plantRooms.map((p, i) => {
      const sp = spans[i], tw = textW(p.name), lo = sp.x + ELEC_BOX_PAD * 2, hi = sp.x + sp.w - ELEC_BOX_PAD;
      const inCell = runs.filter((q) => q.x + q.half > lo && q.x - q.half < hi).sort((a, b) => a.x - b.x);
      const starts = [lo, ...inCell.map((q) => q.x + q.half)];
      const x = starts.find((s0) => s0 >= lo && s0 + tw <= hi && !inCell.some((q) => q.x + q.half > s0 && q.x - q.half < s0 + tw));
      return { rect: { x: x ?? lo, y: y0, w: tw, h: ELEC_TEXT_MM }, ground: x == null };
    });
  };
  // ---- The number badges (decision 17): each where it covers no other wire, no
  // other badge and no box; a wire with no such place opens its corridor's pad,
  // and the grid is laid out down again, at most a few times.
  if (showNumbers) {
    const badgeSize = (text: string): { along: number; across: number } => ({ along: textW(text) * ELEC_BADGE_SCALE + 0.8, across: BADGE_ACROSS });
    const labelRect = (): Rect[] => plantLabels().map((l) => l.rect);
    for (let iter = 0; ; iter++) {
      const obstacles: Rect[] = [...[...pieces.values()].map((p) => ({ x: p.x, y: p.y, w: p.w, h: p.h })), ...labelRect()];
      // The bridges' humps, over the runs that hop.
      for (const w of wiresOut) for (const b of w.bridges) {
        const a = w.points[b.seg], c = w.points[b.seg + 1], level = Math.abs(c.y - a.y) < 1e-9;
        obstacles.push({ x: b.at.x - ELEC_BRIDGE_R, y: b.at.y - ELEC_BRIDGE_R, w: 2 * ELEC_BRIDGE_R, h: level ? ELEC_BRIDGE_R : 2 * ELEC_BRIDGE_R });
      }
      for (const w of wiresOut) if (w.offSheet) obstacles.push(w.offSheet.rect);
      // Upright first: a badge lies along a level run only once its corridor can
      // grow no more.
      const last = iter >= 6;
      const failed = placeElecBadges(wiresOut, obstacles, badgeSize, last, [{ x: gridX, y: view.y, w: ELEC_ROW_HEAD_W, h: gridBottom - view.y }]);
      if (!failed.length || last) break;
      const grow = new Map<string, { c: number; top: boolean; by: number }>();
      for (const w of failed) {
        const pad = padOf.get(w.key);
        if (!pad) continue;
        const k = `${pad.c}:${pad.top}`, by = badgeSize(w.badge!.text).along + 2 * ELEC_BADGE_END + ELEC_BADGE_GAP;
        grow.set(k, { ...pad, by: Math.max(by, grow.get(k)?.by ?? 0) });
      }
      if (!grow.size) { iter = 5; continue; }
      for (const g of grow.values()) (g.top ? padTop : padBottom)[g.c] += g.by;
      wiresOut = down();
    }
  }

  // ---- The grid: rows, columns, cells, borders (decision 20).
  const gridW = gridRight - gridX;
  const rowsOut: ElecLayoutRow[] = visibleRows.map((id) => {
    const r = rowRect.get(id)!;
    return { id, rect: { ...r, w: gridW }, header: { rect: { x: gridX, y: r.y, w: ELEC_ROW_HEAD_W, h: r.h }, text: model.rows.find((x) => x.id === id)!.name } };
  });
  const colRows = visibleRows.filter(isColumnRow);
  const bodyTop = colRows.length ? rowRect.get(colRows[0])!.y : gy0;
  const bodyBottom = colRows.length ? rowRect.get(colRows[colRows.length - 1])!.y + rowRect.get(colRows[colRows.length - 1])!.h : gy0;
  const columnsOut: ElecLayoutColumn[] = columns.map((c) => ({
    id: c.id, text: c.name, color: c.color,
    header: { x: colX.get(c.id)!, y: view.y, w: width.get(c.id)!, h: ELEC_COL_HEAD_H },
    body: { x: colX.get(c.id)!, y: bodyTop, w: width.get(c.id)!, h: bodyBottom - bodyTop },
  }));
  const cells: ElecLayoutCell[] = [];
  const borders: ElecLayoutBorder[] = [];
  const rowCells = (row: ElecRowId, list: Array<{ key: string; x: number; w: number; color?: string; label?: string; labelRect?: Rect; labelGround?: true; id: ElecCellId }>): void => {
    const r = rowRect.get(row)!, { spans, lines } = cellSpans(row, list);
    cells.push(...list.map(({ key: _k, x: _x, w: _w, id, ...c }, i) => ({ id, row, rect: { x: spans[i].x, y: r.y, w: spans[i].w, h: r.h }, ...c })));
    for (const l of lines) borders.push({ row, after: l.after, x: l.x, y0: r.y, y1: r.y + r.h });
  };
  for (const row of colRows) rowCells(row, columns.map((c) => ({ key: c.id, id: elecCellId(row, c.id), x: colX.get(c.id)!, w: width.get(c.id)!, color: c.color })));
  if (rowRect.has('technical')) {
    const labels = plantLabels();
    rowCells('technical', model.plantRooms.map((p, i) => ({ ...plantCells()[i], id: elecCellId('technical', p.id), label: p.name, labelRect: labels[i].rect, ...(labels[i].ground ? { labelGround: true as const } : {}) })));
  }
  if (rowRect.has('client')) rowCells('client', [{ key: '', id: 'client', x: gx0, w: gridRight - gx0 }]);
  const grid: Rect = { x: gridX, y: view.y, w: gridW, h: gridBottom - view.y };
  // The table and the legend stand right of the notes there too, by default.
  const notes = wiresOut.flatMap((w) => (w.offSheet ? [w.offSheet.rect] : []));
  const asideX = Math.max(grid.x + grid.w, ...notes.map((r) => r.x + r.w)) + ELEC_ASIDE_GAP;

  // ---- The table (decision 17) and the legend (decision 25).
  const lang = model.lang;
  const tableWires = [...full, ...stubs].sort((a, b) => compareElecNumbers(a.number, b.number) || a.key.localeCompare(b.key));
  let table: ElecLayoutTable | null = null;
  if (!view.table?.hidden) {
    const header = [tr('No.'), tr('Cable'), tr('Length')];
    const rows = tableWires.map((w) => ({ key: w.key, cells: [w.number, w.cable, w.length != null ? `${elecNum(w.length / 1000, lang)} m` : '—'] }));
    const colW = [10, 30, 14].map((min, i) => Math.max(min, textW(header[i]) + 2 * ELEC_BOX_PAD, ...rows.map((r) => textW(r.cells[i]) + 2 * ELEC_BOX_PAD)));
    const x = view.table?.x ?? asideX, ty = view.table?.y ?? view.y;
    table = { rect: { x, y: ty, w: colW.reduce((s, v) => s + v, 0), h: (rows.length + 1) * ELEC_TABLE_ROW_H }, rowH: ELEC_TABLE_ROW_H, colW, header, rows };
  }
  let legend: ElecLayoutLegend | null = null;
  if (!view.legend?.hidden) {
    const entries: ElecLayoutLegend['entries'] = [];
    const used = new Set([...full, ...stubs].map((w) => w.installedBy));
    if (used.has('stenal')) entries.push({ color: ELEC_STENAL, text: tr('Installed by Stenal'), kind: 'installer' });
    if (used.has('client')) entries.push({ color: ELEC_CLIENT, text: tr('Installed by the client'), kind: 'installer' });
    for (const c of columns) entries.push({ color: c.color, text: c.name, kind: 'column' });
    const w = ELEC_SWATCH + 3 * ELEC_BOX_PAD + Math.max(20, ...entries.map((e) => textW(e.text)));
    const x = view.legend?.x ?? (table ? table.rect.x : asideX);
    const ly = view.legend?.y ?? (table ? table.rect.y + table.rect.h + ELEC_ASIDE_GAP : view.y);
    legend = { rect: { x, y: ly, w, h: Math.max(1, entries.length) * ELEC_TABLE_ROW_H + ELEC_BOX_PAD * 2 }, rowH: ELEC_TABLE_ROW_H, entries };
  }

  const boxesOut: ElecLayoutBox[] = [...pieces.values()].map((p) => ({
    key: p.b.key, box: p.b, rect: { x: p.x, y: p.y, w: p.w, h: p.h }, vertical: p.vertical, angle: p.angle, autoAngle: p.autoAngle, lines: p.b.lines, line: p.line,
    moved: p.b.row === 'client' ? clientMoved.has(p.b.key) : p.b.moved,
  }));
  const rects = [grid, ...notes, ...(table ? [table.rect] : []), ...(legend ? [legend.rect] : [])];
  const bx0 = Math.min(...rects.map((r) => r.x)), by0 = Math.min(...rects.map((r) => r.y));
  const bounds = { x: bx0, y: by0, w: Math.max(...rects.map((r) => r.x + r.w)) - bx0, h: Math.max(...rects.map((r) => r.y + r.h)) - by0 };
  return { bounds, grid, rows: rowsOut, columns: columnsOut, cells, borders, boxes: boxesOut, wires: wiresOut, table, legend, clientAuto };
}

/** Where two wires cross, the more level of the two runs hops over the other —
 *  at right angles, always the jog — and, where both are as level, the later
 *  in number order: a `bridge` on the wire that hops. Not within half a bridge
 *  of either run's ends. */
function placeBridges(wires: ElecLayoutWire[]): void {
  const rank = new Map([...wires].sort((a, b) => compareElecNumbers(a.wire.number, b.wire.number) || a.key.localeCompare(b.key)).map((w, i) => [w.key, i]));
  const level = (a: Vec2, b: Vec2): number => Math.abs(b.y - a.y) / (Math.hypot(b.x - a.x, b.y - a.y) || 1);
  for (let i = 0; i < wires.length; i++) {
    for (let j = i + 1; j < wires.length; j++) {
      const v = wires[i], w = wires[j];
      for (let s = 0; s < v.points.length - 1; s++) {
        for (let t = 0; t < w.points.length - 1; t++) {
          const a = v.points[s], b = v.points[s + 1], c = w.points[t], d = w.points[t + 1];
          const at = segCross(a, b, c, d, ELEC_BRIDGE_R / 2);
          if (!at) continue;
          const lv = level(a, b), lw = level(c, d);
          const vHops = lv < lw - 1e-9 || (Math.abs(lv - lw) <= 1e-9 && rank.get(v.key)! > rank.get(w.key)!);
          (vHops ? v : w).bridges.push({ seg: vHops ? s : t, at });
        }
      }
    }
  }
}

const overlapsRect = (a: Rect, b: Rect): boolean => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
const inflate = (r: Rect, d: number): Rect => ({ x: r.x - d, y: r.y - d, w: r.w + 2 * d, h: r.h + 2 * d });
/** A segment touches a rectangle (Liang–Barsky). */
function segHitsRect(a: Vec2, b: Vec2, r: Rect): boolean {
  let t0 = 0, t1 = 1;
  const dx = b.x - a.x, dy = b.y - a.y;
  const clip = (p: number, q: number): boolean => {
    if (Math.abs(p) < 1e-12) return q >= 0;
    const t = q / p;
    if (p < 0) { if (t > t1) return false; if (t > t0) t0 = t; } else { if (t < t0) return false; if (t < t1) t1 = t; }
    return true;
  };
  return clip(-dx, a.x - r.x) && clip(dx, r.x + r.w - a.x) && clip(-dy, a.y - r.y) && clip(dy, r.y + r.h - a.y) && t0 <= t1;
}

/** Give each wire its number badge (in number order): on its longest upright
 *  run, from the middle out, the first place where it covers no other wire, no
 *  badge placed before it and no obstacle (the boxes, the plant rooms' names,
 *  the off-sheet notes); then its other upright runs, and — `level` — its level
 *  ones (the badge along them). A place over a `shy` one (the row headers, which
 *  a wire off the sheet to the left runs across) only once no other is free. A
 *  wire with no such place still gets one, at its first try, and is returned —
 *  its corridor then makes room (`layoutElecSchematic`). Exported for its test. */
export function placeElecBadges(wires: ElecLayoutWire[], obstacles: Rect[], size: (text: string) => { along: number; across: number }, level: boolean, shy: Rect[] = []): ElecLayoutWire[] {
  const STEP = 0.25;
  const segs = wires.flatMap((w) => w.points.slice(1).map((b, i) => ({ key: w.key, a: w.points[i], b })));
  const placed: Rect[] = [];
  const failed: ElecLayoutWire[] = [];
  const order = [...wires].sort((a, b) => compareElecNumbers(a.wire.number, b.wire.number) || a.key.localeCompare(b.key));
  for (const w of order) {
    const text = w.wire.number;
    const { along, across } = size(text);
    const free = (r: Rect): boolean => {
      const c = inflate(r, ELEC_BADGE_CLEAR), g = inflate(r, ELEC_BADGE_GAP);
      return !segs.some((s) => s.key !== w.key && segHitsRect(s.a, s.b, c)) && !placed.some((o) => overlapsRect(g, o)) && !obstacles.some((o) => overlapsRect(g, o));
    };
    const runs = w.points.slice(1).map((b, i) => ({ a: w.points[i], b, level: Math.abs(b.y - w.points[i].y) < 1e-9 }))
      .filter((r) => level || !r.level)
      .sort((p, q) => Number(p.level) - Number(q.level) || (p.level ? Math.abs(q.b.x - q.a.x) - Math.abs(p.b.x - p.a.x) : Math.abs(q.b.y - q.a.y) - Math.abs(p.b.y - p.a.y)));
    type Spot = { rect: Rect; at: Vec2; vertical: boolean };
    let first: Spot | null = null, got: Spot | null = null;
    // A spot over a `shy` place (the row headers) only once no other is free.
    const later: Spot[] = [];
    for (const { a, b, level } of runs) {
      // Along the run's main axis, the badge's middle kept clear of the run's ends.
      const lo = level ? Math.min(a.x, b.x) : Math.min(a.y, b.y), hi = level ? Math.max(a.x, b.x) : Math.max(a.y, b.y);
      const from = lo + along / 2 + ELEC_BADGE_END, to = hi - along / 2 - ELEC_BADGE_END;
      if (from > to + 1e-9) continue;
      const mid = (from + to) / 2;
      for (let k = 0; mid - k * STEP >= from - 1e-9 || mid + k * STEP <= to + 1e-9; k++) {
        for (const c of k ? [mid - k * STEP, mid + k * STEP] : [mid]) {
          if (c < from - 1e-9 || c > to + 1e-9) continue;
          const t = level ? (c - a.x) / (b.x - a.x) : (c - a.y) / (b.y - a.y);
          const at = level ? { x: c, y: a.y } : { x: a.x + t * (b.x - a.x), y: c };
          const rect = level ? { x: c - along / 2, y: at.y - across / 2, w: along, h: across } : { x: at.x - across / 2, y: c - along / 2, w: across, h: along };
          if (shy.some((o) => overlapsRect(rect, o))) { later.push({ rect, at, vertical: !level }); continue; }
          first ??= { rect, at, vertical: !level };
          if (free(rect)) { got = { rect, at, vertical: !level }; break; }
        }
        if (got) break;
      }
      if (got) break;
    }
    got ??= later.find((o) => free(o.rect)) ?? null;
    first ??= later[0] ?? null;
    if (!got) failed.push(w);
    // No run long enough: across its ends' middle, upright.
    const p0 = w.points[0], p1 = w.points[w.points.length - 1], m = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
    const spot: Spot = got ?? first ?? { at: m, vertical: true, rect: { x: m.x - across / 2, y: m.y - along / 2, w: across, h: along } };
    placed.push(spot.rect);
    w.badge = { at: spot.at, text, rect: spot.rect, vertical: spot.vertical };
  }
  return failed;
}

/** Lanes for the jogs in one corridor (lane 0 at its top), so no two wires
 *  overlap and a wire crosses another only where it must: a wire coming down
 *  inside another's horizontal jogs above it, one going on down inside it jogs
 *  below it; jogs that do not overlap share a lane. Returns the lanes used. */
function assignLanes(items: Array<{ r: Route; tops: number[]; bottoms: number[]; lo: number; hi: number }>): number {
  const n = items.length;
  if (!n) return 0;
  const EPS = 0.05;
  const within = (x: number, it: { lo: number; hi: number }): boolean => x >= it.lo - EPS && x <= it.hi + EPS;
  // Two upright runs nearer than this read as one line: a wire coming down at x
  // jogs above one going on down at (about) x, or the two run on one another
  // between their lanes. That is a must (`hard`); the rest only spare a crossing.
  const NEAR = 0.6;
  const above: boolean[][] = items.map(() => Array(n).fill(false));
  // How near the two runs a must keeps apart come (Infinity: no must).
  const gap: number[][] = items.map(() => Array(n).fill(Infinity));
  const hard: boolean[][] = items.map(() => Array(n).fill(false));
  for (let a = 0; a < n; a++) for (let b = 0; b < n; b++) {
    if (a === b) continue;
    for (const x of items[a].tops) for (const y of items[b].bottoms) if (Math.abs(x - y) < NEAR) gap[a][b] = Math.min(gap[a][b], Math.abs(x - y));
    hard[a][b] = gap[a][b] < NEAR;
    if (hard[a][b] || items[a].tops.some((x) => within(x, items[b])) || items[b].bottoms.some((x) => within(x, items[a]))) above[a][b] = true;
  }
  // A pair that must each be above the other crosses whatever: no constraint —
  // but where one of the two keeps their runs off one another, that one holds
  // (of two such, the one keeping the nearer runs apart).
  for (let a = 0; a < n; a++) for (let b = a + 1; b < n; b++) {
    if (!above[a][b] || !above[b][a]) continue;
    if (gap[a][b] < gap[b][a] - 1e-9) { above[b][a] = false; hard[b][a] = false; }
    else if (gap[b][a] < gap[a][b] - 1e-9) { above[a][b] = false; hard[a][b] = false; }
    else above[a][b] = above[b][a] = hard[a][b] = hard[b][a] = false;
  }
  const indeg = items.map((_, b) => items.reduce((s, _x, a) => s + (above[a][b] ? 1 : 0), 0));
  const indegHard = items.map((_, b) => items.reduce((s, _x, a) => s + (above[a][b] && hard[a][b] ? 1 : 0), 0));
  const done = Array(n).fill(false), order: number[] = [];
  // In order: the fewest musts still above it, then the fewest of the rest,
  // then from the left (a loop of them breaks at a crossing, not on a run; a
  // loop of musts where the runs it leaves side by side are the farthest apart).
  const nearest = (i: number): number => items.reduce((m, _x, a) => (!done[a] && above[a][i] && hard[a][i] ? Math.min(m, gap[a][i]) : m), Infinity);
  const before = (i: number, j: number): boolean => {
    if (indegHard[i] !== indegHard[j]) return indegHard[i] < indegHard[j];
    if (indegHard[i]) { const ni = nearest(i), nj = nearest(j); if (Math.abs(ni - nj) > 1e-9) return ni > nj; }
    return indeg[i] !== indeg[j] ? indeg[i] < indeg[j] : items[i].lo < items[j].lo;
  };
  for (let k = 0; k < n; k++) {
    let pick = -1;
    for (let i = 0; i < n; i++) if (!done[i] && (pick < 0 || before(i, pick))) pick = i;
    done[pick] = true; order.push(pick);
    for (let b = 0; b < n; b++) if (above[pick][b]) { indeg[b]--; if (hard[pick][b]) indegHard[b]--; }
  }
  const lanes: Array<Array<{ lo: number; hi: number }>> = [];
  const laneOf = Array(n).fill(-1);
  const GAP = 0.6;
  for (const i of order) {
    let min = 0;
    for (let a = 0; a < n; a++) if (above[a][i] && laneOf[a] >= 0) min = Math.max(min, laneOf[a] + 1);
    let l = min;
    while (lanes[l]?.some((s) => items[i].lo < s.hi + GAP && s.lo < items[i].hi + GAP)) l++;
    (lanes[l] ??= []).push({ lo: items[i].lo, hi: items[i].hi });
    laneOf[i] = l;
    items[i].r.lane = l;
  }
  return lanes.length;
}

// ---- Fitting a page, picking ------------------------------------------------------------

/** The model's columns split into groups whose grids, with their off-sheet
 *  notes, are each at most `maxWidth` wide (decision 14: what does not fit goes
 *  onto another sheet's view). */
export function splitElecColumns(model: ElecModel, maxWidth: number, opts: ElecLayoutOptions = {}): string[][] {
  const groups: string[][] = [];
  let cur: string[] = [];
  for (const c of model.columns) {
    const next = [...cur, c.id];
    const w = layoutElecSchematic(model, { id: '', x: 0, y: 0, columns: next, showNumbers: false, table: { x: 0, y: 0, hidden: true }, legend: { x: 0, y: 0, hidden: true } }, opts).bounds.w;
    if (w > maxWidth && cur.length) { groups.push(cur); cur = [c.id]; } else cur = next;
  }
  if (cur.length) groups.push(cur);
  return groups;
}

/** How wide a view's columns may lay out, their off-sheet notes with them
 *  (`splitElecColumns`), on a page whose white area ends at `right`: from the
 *  view's corner, less the room its table and legend take past its grid and
 *  notes — the notes counted once. */
export function elecSplitWidth(L: ElecLayout, viewX: number, right: number): number {
  const notesEnd = Math.max(L.grid.x + L.grid.w, ...L.wires.flatMap((w) => (w.offSheet ? [w.offSheet.rect.x + w.offSheet.rect.w] : [])));
  return right - viewX - Math.max(0, L.bounds.x + L.bounds.w - notesEnd);
}

/** What stays picked in a selected view: a box, a wire, a cell, a border, a
 *  header, its table or its legend. */
export type ElecPick =
  | { kind: 'border'; row: ElecRowId; after: string }
  | { kind: 'box'; key: string }
  | { kind: 'cell'; id: ElecCellId }
  | { kind: 'columnHeader'; id: string }
  | { kind: 'legend' }
  | { kind: 'rowHeader'; row: ElecRowId }
  | { kind: 'table' }
  | { kind: 'wire'; key: string };
/** What a press hits: a pick, or a part of a wire — its number, its jog, an end
 *  (with the box it lands on) — which a drag takes hold of and which picks the wire. */
export type ElecHit =
  | ElecPick
  | { kind: 'badge'; key: string }
  | { kind: 'end'; key: string; box: string }
  | { kind: 'jog'; key: string };

/** What a press on `hit` picks: a wire's badge, jog or end picks the wire. */
export function elecPickOf(h: ElecHit): ElecPick {
  return h.kind === 'badge' || h.kind === 'end' || h.kind === 'jog' ? { kind: 'wire', key: h.key } : h;
}

/** How a pick is shown on its view: its wire under the boxes, glowing; a
 *  rectangle outlined (and tinted, `fill`: a cell, a column, a row); a border's
 *  line. Null when it is not in this layout. */
export type ElecPickShape = { wire: string } | { rect: Rect; fill: number } | { line: [Vec2, Vec2] };
export function elecPickShape(L: ElecLayout, p: ElecPick): ElecPickShape | null {
  switch (p.kind) {
    case 'border': { const b = L.borders.find((x) => x.row === p.row && x.after === p.after); return b ? { line: [{ x: b.x, y: b.y0 }, { x: b.x, y: b.y1 }] } : null; }
    case 'box': { const b = L.boxes.find((x) => x.key === p.key); return b ? { rect: b.rect, fill: 0 } : null; }
    case 'cell': { const c = L.cells.find((x) => x.id === p.id); return c ? { rect: c.rect, fill: 0.08 } : null; }
    case 'columnHeader': { const c = L.columns.find((x) => x.id === p.id); return c ? { rect: { ...c.header, h: c.header.h + c.body.h }, fill: 0.06 } : null; }
    case 'legend': return L.legend ? { rect: L.legend.rect, fill: 0 } : null;
    case 'rowHeader': { const r = L.rows.find((x) => x.id === p.row); return r ? { rect: r.rect, fill: 0.06 } : null; }
    case 'table': return L.table ? { rect: L.table.rect, fill: 0 } : null;
    case 'wire': return L.wires.some((w) => w.key === p.key) ? { wire: p.key } : null;
    default: return unreachable(p, 'schematic pick');
  }
}

const inRect = (p: Vec2, r: Rect, tol = 0): boolean => p.x >= r.x - tol && p.x <= r.x + r.w + tol && p.y >= r.y - tol && p.y <= r.y + r.h + tol;
function distSeg(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** The columns of the cable list (the view's table): its wire's number, its
 *  cable, its length. */
export const ELEC_TABLE_COLS = ['number', 'cable', 'length'] as const;
export type ElecTableCol = (typeof ELEC_TABLE_COLS)[number];

/** A cell of the cable list: its row's wire, its column and its rectangle
 *  (paper mm), or null — the header, or off the table. */
export function elecTableCellAt(t: ElecLayoutTable, p: Vec2): { key: string; col: ElecTableCol; rect: Rect } | null {
  if (!inRect(p, t.rect)) return null;
  const i = Math.floor((p.y - t.rect.y) / t.rowH) - 1;
  const row = t.rows[i];
  if (!row) return null;
  let x = t.rect.x;
  for (let c = 0; c < t.colW.length; c++) {
    if (p.x <= x + t.colW[c] || c === t.colW.length - 1) return { key: row.key, col: ELEC_TABLE_COLS[c], rect: { x, y: t.rect.y + (i + 1) * t.rowH, w: t.colW[c], h: t.rowH } };
    x += t.colW[c];
  }
  return null;
}
/** The rectangle (paper mm) of a wire's cell in the cable list, or null. */
export function elecTableCellRect(t: ElecLayoutTable, key: string, col: ElecTableCol): Rect | null {
  const i = t.rows.findIndex((r) => r.key === key), c = ELEC_TABLE_COLS.indexOf(col);
  if (i < 0 || c < 0) return null;
  const x = t.rect.x + t.colW.slice(0, c).reduce((s, w) => s + w, 0);
  return { x, y: t.rect.y + (i + 1) * t.rowH, w: t.colW[c], h: t.rowH };
}

/** How near (paper mm) a press picks a wire's end dot: never less than this. */
export const ELEC_END_PICK = 0.8;

/** What is under a paper point (mm), topmost first: the table, the legend, a
 *  wire's end dot (over the edge of its box), a box, a wire's badge, its jog or
 *  its run, a border, a cell, a header. */
export function elecHitTest(layout: ElecLayout, p: Vec2, tol = 0.8): ElecHit | null {
  if (layout.table && inRect(p, layout.table.rect)) return { kind: 'table' };
  if (layout.legend && inRect(p, layout.legend.rect)) return { kind: 'legend' };
  const near = Math.max(tol, ELEC_END_PICK);
  let end: { d: number; hit: ElecHit } | null = null;
  for (const w of layout.wires) for (const e of w.ends) {
    const d = Math.hypot(p.x - e.at.x, p.y - e.at.y);
    if (d <= near && (!end || d < end.d)) end = { d, hit: { kind: 'end', key: w.key, box: e.box } };
  }
  if (end) return end.hit;
  for (let i = layout.boxes.length - 1; i >= 0; i--) if (inRect(p, layout.boxes[i].rect)) return { kind: 'box', key: layout.boxes[i].key };
  for (const w of layout.wires) if (w.badge && inRect(p, w.badge.rect, 0.2)) return { kind: 'badge', key: w.key };
  for (const w of layout.wires) if (w.jog && Math.abs(p.y - w.jog.y) <= tol && p.x >= w.jog.x0 - tol && p.x <= w.jog.x1 + tol) return { kind: 'jog', key: w.key };
  for (const w of layout.wires) for (let i = 0; i < w.points.length - 1; i++) if (distSeg(p, w.points[i], w.points[i + 1]) <= tol) return { kind: 'wire', key: w.key };
  for (const b of layout.borders) if (Math.abs(p.x - b.x) <= tol && p.y >= b.y0 && p.y <= b.y1) return { kind: 'border', row: b.row, after: b.after };
  for (const r of layout.rows) if (inRect(p, r.header.rect)) return { kind: 'rowHeader', row: r.id };
  for (const c of layout.columns) if (inRect(p, c.header)) return { kind: 'columnHeader', id: c.id };
  for (const c of layout.cells) if (inRect(p, c.rect)) return { kind: 'cell', id: c.id };
  return null;
}

/** The legs (paper mm) of the triangle marking a box made by the schematic itself
 *  (every one but those added by hand), in its top right corner, inside. */
export const ELEC_AUTO_MARK = 1.4;
/** A box the schematic makes from the project (not one added by hand). */
export const elecBoxIsAuto = (b: Pick<ElecLayoutBox, 'box'>): boolean => b.box.kind !== 'hand';
/** A box's auto mark: its corner, then along the top, then down the side. `size`
 *  (paper mm; the screen's few pixels when zoomed out) never more than half the box. */
export function elecAutoMark(r: Rect, size = ELEC_AUTO_MARK): [Vec2, Vec2, Vec2] {
  const m = Math.min(size, r.w / 2, r.h / 2), x = r.x + r.w, y = r.y;
  return [{ x, y }, { x: x - m, y }, { x, y: y + m }];
}
/** The box whose auto mark is under a paper point, if any. */
export function elecAutoMarkAt(layout: ElecLayout, p: Vec2, size = ELEC_AUTO_MARK): string | null {
  for (let i = layout.boxes.length - 1; i >= 0; i--) {
    const b = layout.boxes[i];
    if (!elecBoxIsAuto(b)) continue;
    const [c, , d] = elecAutoMark(b.rect, size), m = d.y - c.y;
    if (p.x <= c.x && p.y >= c.y && (c.x - p.x) + (p.y - c.y) <= m) return b.key;
  }
  return null;
}

/** A layout moved by (dx, dy) paper mm: every rectangle, point and line of it. */
export function translateElecLayout(L: ElecLayout, dx: number, dy: number): ElecLayout {
  const r = (a: Rect): Rect => ({ ...a, x: a.x + dx, y: a.y + dy });
  const p = (a: Vec2): Vec2 => ({ x: a.x + dx, y: a.y + dy });
  return {
    bounds: r(L.bounds),
    grid: r(L.grid),
    rows: L.rows.map((x) => ({ ...x, rect: r(x.rect), header: { ...x.header, rect: r(x.header.rect) } })),
    columns: L.columns.map((x) => ({ ...x, header: r(x.header), body: r(x.body) })),
    cells: L.cells.map((x) => ({ ...x, rect: r(x.rect), ...(x.labelRect ? { labelRect: r(x.labelRect) } : {}) })),
    borders: L.borders.map((x) => ({ ...x, x: x.x + dx, y0: x.y0 + dy, y1: x.y1 + dy })),
    boxes: L.boxes.map((x) => ({ ...x, rect: r(x.rect) })),
    wires: L.wires.map((w) => ({
      ...w,
      points: w.points.map(p),
      bridges: w.bridges.map((b) => ({ ...b, at: p(b.at) })),
      dots: w.dots.map(p),
      ends: w.ends.map((e) => ({ ...e, at: p(e.at) })),
      ...(w.badge ? { badge: { ...w.badge, at: p(w.badge.at), rect: r(w.badge.rect) } } : {}),
      ...(w.jog ? { jog: { y: w.jog.y + dy, x0: w.jog.x0 + dx, x1: w.jog.x1 + dx } } : {}),
      ...(w.offSheet ? { offSheet: { ...w.offSheet, at: p(w.offSheet.at), rect: r(w.offSheet.rect) } } : {}),
    })),
    table: L.table && { ...L.table, rect: r(L.table.rect) },
    legend: L.legend && { ...L.legend, rect: r(L.legend.rect) },
    clientAuto: L.clientAuto,
  };
}
